import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { useProducts } from "../../hooks/useProducts";
import { useCategories } from "../../hooks/useCategories";
import { TableStatus } from "../../components/TableStatus";
import ConfirmationModal from "../../common/ConfirmationModal";
import CustomPagination from "../../common/Pagination";

const STOCK_BADGE = (count) => count > 10 ? "bg-success-subtle text-success" : count > 0 ? "bg-warning-subtle text-warning" : "bg-danger-subtle text-danger";
const STOCK_LABEL = (count) => count > 10 ? "In Stock" : count > 0 ? `Low (${count})` : "Sold Out";

export default function ProductTablePage() {
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [productToDelete, setProductToDelete] = useState(null);

    const { categories } = useCategories();
    const categoryOptions = categories?.map(c => ({ value: c._id, label: c.title })) || [];

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const {
        products,
        pagination,
        isLoading,
        isError,
        error,
        deleteProduct,
        isDeleting
    } = useProducts(page, 12, debouncedSearch, selectedCategory);

    const getProductStock = (product) => {
        if (!product.variants || product.variants.length === 0) return 0;
        return product.variants.reduce((total, variant) => total + (Number(variant.stock) || 0), 0);
    };

    const handleDelete = async () => {
        if (!productToDelete) return;
        await deleteProduct(productToDelete._id);
        setProductToDelete(null);
    };

    const handleCategoryChange = (selectedOption) => {
        setSelectedCategory(selectedOption ? selectedOption.value : "");
        setPage(1); 
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4 p-4">
                <div>
                    <h4 className="mb-0 text-primary-emphasis fw-bold">Products</h4>
                    <p className="text-muted small mb-0">Manage your catalogue, pricing and inventory</p>
                </div>
                <button className="btn btn-success" onClick={() => navigate('/ecommerce/products/new')}>
                    <i className="fas fa-plus mr-2"></i> Add Product
                </button>
            </div>

            <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <h5 className="mb-0 text-dark fw-bold">Product List</h5>

                    <div className="d-flex flex-column flex-sm-row gap-2" style={{ flex: '1 1 auto', maxWidth: '600px', justifyContent: 'flex-end' }}>

                        <div style={{ minWidth: '200px' , marginRight: '1rem' }}>
                            <Select
                                options={categoryOptions}
                                value={categoryOptions.find(c => c.value === selectedCategory) || null}
                                onChange={handleCategoryChange}
                                placeholder="All Categories"
                                isClearable
                                styles={{
                                    control: (provided) => ({
                                        ...provided, backgroundColor: '#f8f9fa', borderColor: '#dee2e6', minHeight: '38px', boxShadow: 'none'
                                    })
                                }}
                            />
                        </div>

                        <div className="input-group" style={{ maxWidth: "300px" }}>
                            <input
                                type="text"
                                className="form-control bg-light border-start-0 ps-0"
                                placeholder="Search products..."
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                            />
                            {searchInput && (
                                <button className="btn btn-light border" type="button" onClick={() => setSearchInput("")}>
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 text-nowrap">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-4">Product</th>
                                    <th>Category</th>
                                    <th>Variants Details</th>
                                    <th>Pricing</th>
                                    <th>Stock</th>
                                    <th className="text-center pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableStatus status={isLoading ? "loading" : isError ? "failed" : "succeeded"} error={error} dataLength={products.length} colSpan={6} />

                                {!isLoading && products.map(p => {
                                    const stock = getProductStock(p);
                                    const hasVariants = p.variants?.length > 0;

                                    const sizeOption = p.availableOptions?.find(opt => opt.name.toLowerCase() === 'size');
                                    const uniqueSizes = sizeOption ? sizeOption.values : [];

                                    const colorOption = p.availableOptions?.find(opt => opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour');
                                    const uniqueColors = colorOption ? colorOption.values : [];

                                    const variantPrices = p.variants?.map(v => Number(v.price)).filter(val => val > 0) || [];
                                    const minComboPrice = variantPrices.length > 0 ? Math.min(...variantPrices) : 0;
                                    const maxComboPrice = variantPrices.length > 0 ? Math.max(...variantPrices) : 0;

                                    const thumbImg = p.images?.[0] || p.variants?.find(v => v.image)?.image;

                                    return (
                                        <tr key={p._id}>
                                            <td className="ps-4">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-light border rounded-3 d-flex align-items-center justify-content-center overflow-hidden flex-shrink-0" style={{ width: "48px", height: "48px" }}>
                                                        {thumbImg ? <img src={thumbImg} alt={p.title} className="w-100 h-100 object-fit-cover" /> : <i className="fas fa-box text-muted"></i>}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-bold">{p.title}</h6>
                                                        {p.sku ? <small className="text-muted">Base SKU: {p.sku}</small> : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {p.category?.title
                                                    ? <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-1">{p.category.title}</span>
                                                    : <span className="text-muted">—</span>
                                                }
                                            </td>

                                            <td>
                                                {hasVariants ? (
                                                    <div className="d-flex flex-column gap-2 py-1">
                                                        {uniqueSizes.length > 0 && (
                                                            <div className="d-flex flex-wrap align-items-center gap-1">
                                                                <span className="small text-muted fw-bold me-1">Size:</span>
                                                                {uniqueSizes.map((sz, i) => (
                                                                    <span key={i} className="badge bg-light border text-dark">
                                                                        {sz.value} {sz.price > 0 && <span className="text-success ms-1">(+₹{sz.price})</span>}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {uniqueColors.length > 0 && (
                                                            <div className="d-flex flex-wrap align-items-center gap-1">
                                                                <span className="small text-muted fw-bold me-1">Color:</span>
                                                                {uniqueColors.slice(0, 4).map((col, i) => (
                                                                    <div key={i} className="d-flex align-items-center bg-light border rounded px-1" style={{ height: "22px" }}>
                                                                        <span className="rounded-circle border shadow-sm" style={{ width: "12px", height: "12px", backgroundColor: col.value }} title={col.value} />
                                                                        {col.price > 0 && <span className="ms-1 text-success fw-bold" style={{ fontSize: "10px" }}>+₹{col.price}</span>}
                                                                    </div>
                                                                ))}
                                                                {uniqueColors.length > 4 && <span className="small text-muted">+{uniqueColors.length - 4}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">Standard Product</span>
                                                )}
                                            </td>

                                            <td>
                                                <div className="d-flex flex-column">
                                                    <div className="small text-muted mb-1" style={{ fontSize: '12px' }}>
                                                        Base: {p.comparePrice && <span className="text-decoration-line-through me-1">₹{Number(p.comparePrice).toFixed(2)}</span>}
                                                        <strong className="text-dark">₹{Number(p.price || 0).toFixed(2)}</strong>
                                                    </div>
                                                    {hasVariants && variantPrices.length > 0 && (
                                                        <div className="small fw-bold text-primary bg-primary-subtle px-2 py-1 rounded d-inline-block" style={{ width: 'max-content' }}>
                                                            Combos: {minComboPrice !== maxComboPrice
                                                                ? `₹${minComboPrice.toFixed(2)} - ₹${maxComboPrice.toFixed(2)}`
                                                                : `₹${minComboPrice.toFixed(2)}`
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="d-flex flex-column align-items-start gap-1">
                                                    <span className={`badge rounded-pill ${STOCK_BADGE(stock)}`}>{STOCK_LABEL(stock)}</span>
                                                    {hasVariants && <small className="text-muted" style={{ fontSize: "11px" }}>{stock} total items</small>}
                                                </div>
                                            </td>
                                            <td className="text-center pe-4">
                                                <div className="d-flex justify-content-center gap-2">
                                                    <button className="btn btn-sm btn-outline-primary px-3 mr-2" onClick={() => navigate(`/ecommerce/products/edit/${p._id}`)}>Edit</button>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => setProductToDelete(p)}><i className="fas fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {pagination?.totalPages > 1 && (
                    <div className="card-footer bg-white">
                        <CustomPagination
                            currentPage={Number(page)}
                            totalPages={Number(pagination.totalPages)}
                            totalItems={Number(pagination.count)}
                            itemsPerPage={12}
                            onPageChange={setPage}
                        />
                    </div>
                )}
            </div>

            <ConfirmationModal
                show={productToDelete !== null}
                onClose={() => setProductToDelete(null)}
                onConfirm={handleDelete}
                title="Confirm Deletion"
                confirmText="Delete"
                confirmButtonVariant="danger"
                isLoading={isDeleting}
            >
                Are you sure you want to delete the product <strong>{productToDelete?.title}</strong>? This cannot be undone.
            </ConfirmationModal>
        </>
    );
}